import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, User } from 'lucide-react';
import { clientsAPI, usersAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function AssignClientModal({ client, isOpen, onClose, preSelectedTechnicianId = null }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    technicianId: preSelectedTechnicianId || '',
    notes: ''
  });

  // Update form when preSelectedTechnicianId changes
  useEffect(() => {
    if (preSelectedTechnicianId) {
      setFormData(prev => ({ ...prev, technicianId: preSelectedTechnicianId }));
    }
  }, [preSelectedTechnicianId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        technicianId: '',
        notes: ''
      });
    }
  }, [isOpen]);

  // Fetch technicians
  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }),
    { enabled: isOpen }
  );

  // Assignment mutation
  const assignMutation = useMutation(
    (data) => clientsAPI.assignContractClient(data),
    {
      onSuccess: () => {
        toast.success('Customer assigned successfully!');
        queryClient.invalidateQueries('contractClientsNeedingService');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
        queryClient.invalidateQueries('allActiveClients');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign customer');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.technicianId) {
      toast.error('Please select a technician');
      return;
    }

    assignMutation.mutate({
      clientId: client.id,
      technicianId: formData.technicianId,
      notes: formData.notes,
      ...(client.serviceCallId ? { serviceCallId: client.serviceCallId } : {})
    });
  };

  if (!isOpen) return null;

  const technicians = techniciansData?.data?.users || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Assign Customer to Technician
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">
                    {client?.isCommercial && client?.companyName 
                      ? client.companyName 
                      : `${client?.contactFirstName} ${client?.contactLastName}`}
                  </span>
                  <span className="mx-2">•</span>
                  {client?.city}, {client?.state}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="px-4 py-5 sm:p-6 space-y-6">
              {/* Technician Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  Technician *
                </label>
                <select
                  value={formData.technicianId}
                  onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a technician</option>
                  {technicians
                    .filter(tech => tech.isActive)
                    .map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.firstName} {tech.lastName} {tech.email}
                      </option>
                    ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any special instructions or notes..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                type="submit"
                disabled={assignMutation.isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignMutation.isLoading ? 'Assigning...' : 'Assign Customer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}








